<template>
  <div class="page-wrapper">
    <!-- Success State -->
    <div v-if="subscriptionStatus === 'success'" class="success-wrapper">
      <section class="hero-panel">
        <img :src="require(`../../brand/${this.brandConfig.brand}/assets/evina-img.webp`)" class="hero-image" alt="Battle League Gaming hero image">
        <div class="hero-shadow"></div>
        <div class="overlay-image-wrapper">
          <img :src="require(`../../brand/${this.brandConfig.brand}/assets/logo-caption.png`)" class="overlay-image" alt="Battle League Gaming">
        </div>
      </section>

      <section class="content-panel">
        <div class="success-header">
          Success!
        </div>
        <div class="success-message">
          Thank you for joining {{ this.brandConfig.tabTitle }}!
        </div>

        <div class="cta-link" @click="redirectToGame">
          Go to {{ this.brandConfig.tabTitle }}
        </div>
      </section>
    </div>

    <!-- Error State -->
    <div v-else-if="subscriptionStatus === 'error'" class="error-wrapper">
      <section class="hero-panel">
        <div class="error-hero-background"></div>
      </section>

      <section class="content-panel error-content-panel">
        <div class="error-icon">✕</div>
        <div class="error-header">
          Subscription Failed
        </div>
        <div class="error-message">
          {{ errorDetails.message }}
        </div>
        <div class="error-code">
          Error Code: {{ errorDetails.code }}
        </div>
      </section>
    </div>

    <!-- Loading State -->
    <div v-else class="loading-wrapper">
      <section class="hero-panel">
        <div class="loading-hero-background"></div>
      </section>

      <section class="content-panel loading-content-panel">
        <div class="spinner"></div>
        <div class="loading-header">
          Processing your subscription...
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import OnevasService from "@/services/onevasService";
import CampaignService from "@/services/campaignService";
import { triggerGTMEvent } from "@/functions/addGTM";
import { v4 as guid } from "uuid";
import {
  parseSubscriptionResponse,
  getErrorMessage,
  generateBasicAuth,
  getCrossSellConfig
} from "@/utils/subscriptionHelpers";
import { ethiotelConfig } from "@/config";
import parseTransactionId from "@/functions/parseTransactionId";

export default {
  data() {
    return {
      subscriptionStatus: 'loading', // 'loading', 'success', 'error'
      errorDetails: {
        code: null,
        message: null
      },
      correlationId: null,
      msisdn: null,
      code: null,
      message: null,
      crossSellConfig: null,
      isCrossSellLoading: false
    }
  },
  computed: {
    ...mapState(['brandConfig'])
  },
  methods: {
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);

      this.correlationId = urlParams.get("correlationId") || urlParams.get("correlatorId");
      this.code = urlParams.get("code");
      this.message = urlParams.get("message");
      this.msisdn = urlParams.get("msisdn");

      return {
        correlationId: this.correlationId,
        code: this.code,
        message: this.message,
        msisdn: this.msisdn
      };
    },

    async handleSubscriptionResponse() {
      try {
        const params = this.parseUrlParams();
        // Check if subscription was successful based on code
        const response = parseSubscriptionResponse(params.code, params.message);
        if (!response.status === 'success') {
          // Handle error
          this.subscriptionStatus = 'error';
          this.errorDetails = {
            code: params.code,
            message: params.message || getErrorMessage(params.code)
          };
          return;
        }

        // Success - Upsert user as ACTIVE
        console.log('=== Processing Successful Subscription ===');

        if (params.correlationId) {
          try {
            await OnevasService.upsertUserViaProductId(
                params.correlationId,
                params.msisdn,
                this.brandConfig?.serviceId,
                this.brandConfig.productId,
                this.brandConfig?.campaignId,
                null,
            );
            console.log('✓ User upserted successfully');
            this.subscriptionStatus = 'success';
            // Load cross-sell configuration
            this.crossSellConfig = getCrossSellConfig(this.brandConfig.brand);
              // Redirect To Game
              this.redirectToGame();
          } catch (upsertError) {
            console.error('Error:', upsertError);
            console.error('Response:', upsertError.response);
            this.subscriptionStatus = 'success';

            setTimeout(() => {
              this.$router.push('/');
            }, 3000);
          }
        } else {
          console.warn('No correlation ID provided, skipping upsert');

          setTimeout(() => {
            this.$router.push('/');
          }, 3000);
        }
      } catch (error) {
        console.error('=== Fatal Error in handleSubscriptionResponse ===');
        console.error('Error:', error);

        this.subscriptionStatus = 'error';
        this.errorDetails = {
          code: -1,
          message: 'An unexpected error occurred. Please try again.'
        };
      }
    },

    async handleCrossSellSubscription() {
      if (!this.crossSellConfig) return;

      this.isCrossSellLoading = true;

      console.log('=== Cross-sell Subscription Initiated ===');
      console.log('Target Product:', this.crossSellConfig.brand);
      console.log('Target Product ID:', this.crossSellConfig.productId);
      console.log('Target Campaign ID:', this.crossSellConfig.campaignId);

      try {
        triggerGTMEvent('cross_sell_subscribe_button_clicked', {
          from_product: this.brandConfig.productId,
          to_product: this.crossSellConfig.productId
        });

        const urlParams = new URLSearchParams(window.location.search);
        const parsedTransactionId = parseTransactionId(urlParams.get('transaction_id'));
        const correlationId = guid().toString();

        // Get campaign config for cross-sell product
        const {
          clickIdParameterName,
          publisherIdParameterName,
        } = await CampaignService.getCampaignConfig(this.crossSellConfig.campaignId);

        const publisherId = urlParams.get(publisherIdParameterName) ?? urlParams.get('publisherId') ?? parsedTransactionId?.publisherId;
        const clickId = parsedTransactionId?.clickId ??
            urlParams.get(clickIdParameterName) ??
            urlParams.get('clickid') ??
            urlParams.get('clickId') ??
            urlParams.get('click_id') ??
            urlParams.get('ofreqid') ??
            urlParams.get('transaction_id') ??
            urlParams.get('pr_mbp_clickID');

        console.log('=== Cross-sell Outbound Data ===');
        console.log('Correlation ID:', correlationId);
        console.log('MSISDN:', this.msisdn);
        console.log('Product ID:', this.crossSellConfig.productId);
        console.log('Campaign ID:', this.crossSellConfig.campaignId);
        console.log('Publisher ID:', publisherId);
        console.log('Click ID:', clickId);
        console.log('==============================');

        // Queue subscription for cross-sell product
        await OnevasService.queueUserSubscription(
            correlationId,
            this.msisdn,
            this.crossSellConfig.productId,
            this.crossSellConfig.campaignId,
            clickId,
            publisherId,
            undefined,
            undefined
        );

        console.log('✓ Cross-sell subscription queued successfully');

        // Build redirect URL with all parameters
        const authorization = generateBasicAuth();
        const redirectUrl = `${ethiotelConfig.redirectUrl}?correlationId=${correlationId}&trxId=${correlationId}&mcc=${this.crossSellConfig.mcc}&mnc=${this.crossSellConfig.mnc}&spId=${this.crossSellConfig.spId}&serviceId=${this.crossSellConfig.serviceId}&productId=${this.crossSellConfig.productId}&authorization=${authorization}&entryChannel=Web`;

        console.log('=== Cross-sell Redirect URL ===');
        console.log(redirectUrl);
        console.log('==============================');

        window.location.href = redirectUrl;

      } catch (error) {
        console.error('=== Cross-sell Subscription Error ===');
        console.error('Error:', error);
        console.error('Response:', error.response);
        console.error('===================================');

        alert('Failed to process cross-sell subscription. Please try again.');
      } finally {
        this.isCrossSellLoading = false;
      }
    },

    redirectToGame() {
      const params = this.parseUrlParams();
      const gameUrl = `${this.brandConfig.gameUrl}?msisdn=${params.msisdn}`;
      console.log('=== Redirecting to Game ===');
      console.log('Game URL:', gameUrl);

      triggerGTMEvent('redirect_to_game', {
        game_url: gameUrl,
        product_id: this.brandConfig.productId
      });

      window.location.href = gameUrl;
    }
  },

  async mounted() {
    console.log('=== Thank You Page Mounted ===');
    triggerGTMEvent('thank_you_page_view');

    await this.handleSubscriptionResponse();
  }
}
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background: #020202;
  color: #FFFFFF;
  display: flex;
  flex-direction: column;
}

.success-wrapper,
.error-wrapper,
.loading-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.hero-panel {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  min-height: 64vh;
  max-height: 74vh;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.hero-shadow {
  position: absolute;
  inset: 0;
  background:
      linear-gradient(180deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0) 14%, rgba(0, 0, 0, 0.18) 58%, rgba(2, 2, 2, 0.96) 100%),
      linear-gradient(180deg, rgba(2, 2, 2, 0) 0%, rgba(2, 2, 2, 0.9) 92%, #020202 100%);
}

.error-hero-background,
.loading-hero-background {
  width: 100%;
  min-height: 64vh;
  max-height: 74vh;
  background: linear-gradient(180deg, #1a1a1a 0%, #020202 100%);
  display: block;
}

.overlay-image-wrapper {
  position: absolute;
  left: 50%;
  bottom: 25%;
  width: min(84%, 360px);
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 2;
}

.overlay-image {
  width: 100%;
  height: auto;
  display: block;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.5));
}

.content-panel {
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin-top: -8rem;
  padding: 0 1.15rem 2rem;
  position: relative;
  z-index: 3;
}

.error-content-panel,
.loading-content-panel {
  margin-top: -4rem;
  padding: 2rem 1.15rem;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.success-header {
  color: #FFFFFF;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
}

.success-message {
  color: #C4C4C4;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  max-width: 20rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}

.cta-link {
  color: #B3F50D;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.3s ease;
}

.cta-link:hover {
  opacity: 0.8;
}

.error-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  background: #f44336;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  font-weight: bold;
}

.error-header {
  color: #FFFFFF;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
}

.error-message {
  color: #C4C4C4;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  max-width: 20rem;
}

.error-code {
  color: #FFFFFF;
  text-align: center;
  font-size: 0.9rem;
  opacity: 0.8;
  font-family: monospace;
}

.spinner {
  width: 60px;
  height: 60px;
  margin: 0 auto;
  border: 6px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-header {
  color: #FFFFFF;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (min-width: 768px) {
  .hero-image,
  .error-hero-background,
  .loading-hero-background {
    min-height: 70vh;
  }

  .content-panel {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
</style>
